import React, { useState, useEffect, useRef } from 'react';

const sciButtonLayout = [
  ['(', ')', 'MC', 'MR', 'M+'],
  ['sin', 'cos', 'tan', 'ln', 'log'],
  ['π', 'e', '√', '^', '%'],
  ['7', '8', '9', '/'],
  ['4', '5', '6', '*'],
  ['1', '2', '3', '-'],
  ['0', '.', '=', '+'],
];

const allowedKeys = new Set(['0','1','2','3','4','5','6','7','8','9','.','+','-','*','/','Enter','=','Backspace','Delete','c','C','(',')','^','%']);

const SCI_FUNCS = {
  sin: (x: number) => Math.sin(x),
  cos: (x: number) => Math.cos(x),
  tan: (x: number) => Math.tan(x),
  ln: (x: number) => Math.log(x),
  log: (x: number) => Math.log10(x),
  sqrt: (x: number) => Math.sqrt(x),
};

const Calculator: React.FC = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [memory, setMemory] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleButtonClick = (value: string) => {
    if (value === 'C') {
      setInput('');
      setResult(null);
    } else if (value === '⌫') {
      setInput((prev) => prev.slice(0, -1));
    } else if (value === '=') {
      try {
        const expr = input
          .replace(/π/g, Math.PI.toString())
          .replace(/e/g, Math.E.toString())
          .replace(/√/g, 'sqrt')
          .replace(/\^/g, '**')
          .replace(/%/g, '/100')
          .replace(/sin\(/g, 'Math.sin(')
          .replace(/cos\(/g, 'Math.cos(')
          .replace(/tan\(/g, 'Math.tan(')
          .replace(/ln\(/g, 'Math.log(')
          .replace(/log\(/g, 'Math.log10(');
        // eslint-disable-next-line no-eval
        const evalResult = eval(expr);
        setResult(evalResult.toString());
        setHistory((h) => [input + ' = ' + evalResult, ...h.slice(0, 9)]);
      } catch (e) {
        setResult('Error');
      }
    } else if (value === 'MC') {
      setMemory(0);
    } else if (value === 'MR') {
      setInput((prev) => prev + memory.toString());
    } else if (value === 'M+') {
      try {
        // eslint-disable-next-line no-eval
        const evalResult = eval(input);
        setMemory((m) => m + Number(evalResult));
      } catch { /* intentionally left empty */ }
    } else if (value === '√') {
      setInput((prev) => prev + '√(');
    } else if (['sin', 'cos', 'tan', 'ln', 'log'].includes(value)) {
      setInput((prev) => prev + value + '(');
    } else if (value === 'π') {
      setInput((prev) => prev + 'π');
    } else if (value === 'e') {
      setInput((prev) => prev + 'e');
    } else if (value === '^') {
      setInput((prev) => prev + '^');
    } else if (value === '%') {
      setInput((prev) => prev + '%');
    } else if (value === 'copy') {
      if (result !== null) navigator.clipboard.writeText(result);
    } else {
      if (result !== null) {
        setInput(value.match(/[0-9.]/) ? value : result + value);
        setResult(null);
      } else {
        setInput((prev) => prev + value);
      }
    }
  };

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!allowedKeys.has(e.key)) return;
      if (e.key === 'Enter' || e.key === '=') {
        handleButtonClick('=');
        e.preventDefault();
      } else if (e.key === 'Backspace') {
        handleButtonClick('⌫');
        e.preventDefault();
      } else if (e.key === 'Delete' || e.key === 'c' || e.key === 'C') {
        handleButtonClick('C');
        e.preventDefault();
      } else {
        handleButtonClick(e.key);
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [input, result]);

  return (
    <div ref={containerRef} className="w-full max-w-xs mx-auto select-none font-[var(--font-family)]">
      <div className="rounded-lg p-4 mb-4 text-right text-2xl font-mono min-h-[56px] bg-[var(--color-surface)] text-[var(--color-text)] border border-gray-200 dark:border-gray-600 shadow-md flex items-center justify-between">
        <span>{result !== null ? <span className="text-[var(--color-primary)]">{result}</span> : input || '0'}</span>
        <button
          className="ml-2 px-2 py-1 rounded bg-[var(--color-primary-light)] text-[var(--color-primary)] text-xs font-semibold hover:bg-[var(--color-primary)] hover:text-white transition-colors"
          onClick={() => handleButtonClick('copy')}
          title="Copy result"
          disabled={result === null}
        >
          Copy
        </button>
      </div>
      <div className="mb-2 max-h-20 overflow-y-auto text-xs text-[var(--color-text-secondary)]">
        {history.length > 0 && (
          <ul>
            {history.map((h, i) => (
              <li key={i}>{h}</li>
            ))}
          </ul>
        )}
      </div>
      <div className="grid grid-cols-5 gap-2 mb-2">
        {sciButtonLayout[0].map((btn, i) => (
          <button
            key={i}
            className="rounded-lg py-2 text-base font-semibold bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-primary-light)] dark:bg-[var(--color-surface-dark)] dark:text-[var(--color-text)] dark:hover:bg-[var(--color-primary-dark)] border border-gray-200 dark:border-gray-600 shadow-sm"
            onClick={() => handleButtonClick(btn)}
          >
            {btn}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-5 gap-2 mb-2">
        {sciButtonLayout[1].map((btn, i) => (
          <button
            key={i}
            className="rounded-lg py-2 text-base font-semibold bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-primary-light)] dark:bg-[var(--color-surface-dark)] dark:text-[var(--color-text)] dark:hover:bg-[var(--color-primary-dark)] border border-gray-200 dark:border-gray-600 shadow-sm"
            onClick={() => handleButtonClick(btn)}
          >
            {btn}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-5 gap-2 mb-2">
        {sciButtonLayout[2].map((btn, i) => (
          <button
            key={i}
            className="rounded-lg py-2 text-base font-semibold bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-primary-light)] dark:bg-[var(--color-surface-dark)] dark:text-[var(--color-text)] dark:hover:bg-[var(--color-primary-dark)] border border-gray-200 dark:border-gray-600 shadow-sm"
            onClick={() => handleButtonClick(btn)}
          >
            {btn}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2 mb-2">
        {sciButtonLayout[3].map((btn, i) => (
          <button
            key={i}
            className="bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-primary-light)] rounded-lg py-3 text-xl font-semibold shadow-sm transition-colors border border-gray-200 dark:border-gray-600 dark:bg-[var(--color-surface-dark)] dark:text-[var(--color-text)] dark:hover:bg-[var(--color-primary-dark)]"
            onClick={() => handleButtonClick(btn)}
          >
            {btn}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2 mb-2">
        {sciButtonLayout[4].map((btn, i) => (
          <button
            key={i}
            className="bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-primary-light)] rounded-lg py-3 text-xl font-semibold shadow-sm transition-colors border border-gray-200 dark:border-gray-600 dark:bg-[var(--color-surface-dark)] dark:text-[var(--color-text)] dark:hover:bg-[var(--color-primary-dark)]"
            onClick={() => handleButtonClick(btn)}
          >
            {btn}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2 mb-2">
        {sciButtonLayout[5].map((btn, i) => (
          <button
            key={i}
            className="bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-primary-light)] rounded-lg py-3 text-xl font-semibold shadow-sm transition-colors border border-gray-200 dark:border-gray-600 dark:bg-[var(--color-surface-dark)] dark:text-[var(--color-text)] dark:hover:bg-[var(--color-primary-dark)]"
            onClick={() => handleButtonClick(btn)}
          >
            {btn}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2">
        {sciButtonLayout[6].map((btn, i) => (
          <button
            key={i}
            className={`bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-primary-light)] rounded-lg py-3 text-xl font-semibold shadow-sm transition-colors border border-gray-200 dark:border-gray-600 dark:bg-[var(--color-surface-dark)] dark:text-[var(--color-text)] dark:hover:bg-[var(--color-primary-dark)] ${btn === '=' ? 'col-span-1 bg-[var(--color-primary)] text-[var(--color-text)] hover:bg-[var(--color-primary-dark)] dark:bg-[var(--color-primary)] dark:text-[var(--color-text)] dark:hover:bg-[var(--color-primary-dark)]' : ''}`}
            onClick={() => handleButtonClick(btn)}
          >
            {btn}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Calculator; 